import {
  ChevronDownIcon,
  ChevronUpIcon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  EyeSlashIcon,
  PrinterIcon,
  RectangleStackIcon,
  Squares2X2Icon,
  TableCellsIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';
import { useEffect, useState } from 'react';
import type { FormSchemaV2, PageBreak, SectionKind, SectionPresentation, TableColumnEmphasis } from '../../lib/agentShared/formSchema';
import { reportLayoutOf, resolveSectionPresentation } from '../../lib/agentShared/formSchema';
import {
  Badge, Callout, Cluster, ContextHeader, Input, RowActions, SegmentedControl, Stack, Surface, Switch, ToolbarActions,
} from '../ui';
import { availableContentHeightMm } from '../../report-document';
import {
  ReportDesignerSettings,
  ResetConfirmDialog,
} from '../formBuilder/ReportDesigner';
import { currentReportOrderIds } from '../formBuilder/reportStudio/reportStudioCommands';
import type { ReportStudioCommand } from '../formBuilder/reportStudio/reportStudioCommands';
import type { ReportPresentation } from '../formBuilder/reportStudio/reportPresentationSchema';

// Story 4-114 — the three-way page-flow picker's option list, same labels as
// `EditorCanvas.tsx`'s own per-section control (`PAGE_BREAK_OPTIONS`) — one
// vocabulary for the same underlying `pageBreak` field, whichever surface a
// template author reaches it from.
const PAGE_BREAK_OPTIONS: Array<{ value: PageBreak; label: string }> = [
  { value: 'auto', label: 'Auto' },
  { value: 'always', label: 'Break before' },
  { value: 'avoid', label: 'Keep with previous' },
];

/**
 * Story 4-114 edge case — "a section configured to avoid splitting is
 * taller than one page" must be DISABLED with an explanation, not silently
 * ignored. A REAL DOM measurement of the section as it currently renders on
 * the canvas (`ReportRender`/`PagedReportPreview` both stamp
 * `data-report-section-id` on every section, story 4-113) compared against
 * the real usable content height for the document's current page size/
 * orientation/margin preset/letterhead mode (`availableContentHeightMm`, the
 * SAME resolver the margin-preset safety check uses) — not a guess from the
 * section's field count. Re-measures whenever the schema or any of those
 * document-level settings change (the canvas re-renders in the same commit,
 * so this effect always sees the up-to-date DOM). Scoped to whichever
 * preview mode the canvas is currently showing — the continuous 'web' mode
 * gives the most reliable single-element measurement; 'print' mode COULD, in
 * principle, expose the same section split across a Paged.js page boundary,
 * in which case only the first fragment is measured (an honest limitation,
 * not a silent wrong answer: it can only under-report an overflow, and a
 * section that already overflows one page almost always still overflows the
 * first fragment alone).
 */
function useSectionOverflowsPage(sectionId: string, schema: FormSchemaV2): boolean {
  const layout = reportLayoutOf(schema);
  const [overflows, setOverflows] = useState(false);
  useEffect(() => {
    const selector = `[data-report-section-id="${CSS.escape(sectionId)}"]`;
    const el = document.querySelector<HTMLElement>(selector);
    if (!el) {
      setOverflows(false);
      return;
    }
    const heightPx = el.getBoundingClientRect().height;
    const availableMm = availableContentHeightMm({
      pageSize: layout.pageSize,
      orientation: layout.pageOrientation,
      marginsPreset: layout.marginsPreset,
      letterheadMode: layout.letterhead.mode,
    });
    setOverflows(heightPx > availableMm * (96 / 25.4));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionId, schema, layout.pageSize, layout.pageOrientation, layout.marginsPreset, layout.letterhead.mode]);
  return overflows;
}

// Report Studio's own outline + inspector (story 4-113) — replaces the old
// `TemplateReportTools` (a bare Style/Content TAB switcher, no real report
// structure) and makes `ReportDesignerSettings` (Style/Content — unchanged
// internally beyond the `dispatch` refactor, see that file's own comment)
// one SELECTION among several, rather than the entire right panel at all
// times. This is the "two-panel layout" the `4-112` retirement note and
// this story's own opening line describe replacing: a tool-switcher rail +
// a bulk settings panel becomes a real, section-addressable OUTLINE driving
// a SELECTION-scoped inspector — the shell's own interaction contract
// ("Tool panels change context on selection and must never sit filled with
// unrelated controls").
//
// Scope boundary (read `docs/prd/17-story-map.md`'s own `4-113` entry
// before extending this file): selecting a SECTION shows only a minimal,
// honest placeholder — real per-section presentation controls
// (layout/columns/density/heading/pageBreak/…) are `4-116`'s own job, not
// this foundation story's. Selecting the DOCUMENT (the default, and the one
// real, fully-working panel today) mounts the pre-existing
// `ReportDesignerSettings` Style/Content tabs UNCHANGED beyond the
// typed-command refactor — "a lot of existing, working logic... has to keep
// working somewhere, not be deleted" (this story's own brief). Table/image/
// finding-level selection is deferred to `4-118`/`4-119`, whose own
// inspector panels are the first ones with anything real to show for those
// targets — there is nothing to build here yet without inventing UI ahead
// of its own story.
export type ReportStudioSelection =
  | { kind: 'document' }
  | { kind: 'section'; sectionId: string }
  | { kind: 'coverPage' };

// Story 4-114 — 'layout' is the Document Tools tab (page format,
// orientation, margin preset, cover page, table of contents). Story 4-115 —
// 'headerFooter' is the Header/Footer Tools tab (letterhead slots, layout
// density, confidentiality, text color). Story 4-119 — 'media' is the Media
// Tools tab (media-summary layout/image-fit/captions/timestamps/grouping) —
// a REPORT-WIDE tool group, like 'layout'/'headerFooter', not a per-section
// one, so it lives here rather than in the section inspector below.
export type ReportSettingsTab = 'style' | 'content' | 'layout' | 'headerFooter' | 'media';
export type ReportPreviewMode = 'web' | 'print';

const SECTION_ICON: Record<SectionKind, ComponentType<SVGProps<SVGSVGElement>>> = {
  section: Squares2X2Icon,
  page: RectangleStackIcon,
  table: TableCellsIcon,
};

const navigationActionClass =
  'min-w-0 [&>button]:min-w-0 [&>button]:max-w-full [&>button]:overflow-hidden [&>button]:text-ellipsis';

export interface ReportStudioOutlineProps {
  schema: FormSchemaV2;
  selection: ReportStudioSelection;
  onSelect: (selection: ReportStudioSelection) => void;
  // Story 4-114 — "reorder report sections independently from capture
  // order" needs a keyboard-accessible Move up/Move down alternative right
  // where sections are listed (4-113's own interaction contract: "dragging
  // supported where safe with keyboard-accessible alternatives") — the
  // outline did not need to mutate anything before this story.
  dispatch: (command: ReportStudioCommand) => void;
}

/**
 * Left panel: "report outline, page structure, reusable blocks" (this
 * story's own header comment). Structure is real (every template section,
 * click-to-select, mirrors `TemplateEditorOutline`'s own section list so the
 * Build and Report tabs read as the SAME kind of document outline); "Blocks"
 * is an honest empty state today — `blocks: ReportBlock[]` exists in the
 * schema (`reportPresentationSchema.ts`) but no tool-group story populates
 * one yet (that lands with `4-117`'s "insert controlled static blocks").
 */
export function ReportStudioOutline({ schema, selection, onSelect, dispatch }: ReportStudioOutlineProps): JSX.Element {
  const layout = reportLayoutOf(schema);
  // Story 4-114 — the outline lists sections in REPORT order (what a reader
  // will actually see), independent of `schema.sections`' own capture order
  // — the SAME resolution `currentReportOrderIds` computes for the reorder
  // commands below, so "what you see here" and "what Move up/down operates
  // on" can never disagree. Hidden sections stay listed (an author needs to
  // find a hidden section to un-hide it) but are visually de-emphasized and
  // labeled, matching this story's "hide without removing from capture"
  // contract — hidden only affects the REPORT, never this authoring view.
  const orderedIds = currentReportOrderIds(schema);
  const sectionsById = new Map(schema.sections.map((section) => [section.id, section]));
  const orderedSections = orderedIds.map((id) => sectionsById.get(id)).filter((s): s is FormSchemaV2['sections'][number] => Boolean(s));

  const moveSection = (sectionId: string, direction: 'up' | 'down') => {
    const ids = currentReportOrderIds(schema);
    const index = ids.indexOf(sectionId);
    const swapWith = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || swapWith < 0 || swapWith >= ids.length) return;
    const next = [...ids];
    const a = next[index];
    const b = next[swapWith];
    if (a === undefined || b === undefined) return;
    next[index] = b;
    next[swapWith] = a;
    dispatch({
      type: 'setSectionReportOrder',
      label: `Move "${sectionsById.get(sectionId)?.name || 'section'}" ${direction}`,
      payload: { orderedSectionIds: next },
    });
  };

  return (
    <Surface as="section" variant="bare" className="min-h-full" data-testid="report-studio-outline">
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader
          title="Report outline"
          headingLevel={2}
          action={<span className="text-caption text-muted-foreground">{schema.sections.length} sections</span>}
        />
      </Surface>
      <Stack as="nav" aria-label="Report outline" gap={4} className="p-3">
        <Stack gap={1}>
          <span className="px-2 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
            Document
          </span>
          <ToolbarActions
            size="sm"
            maxVisible={1}
            className={navigationActionClass}
            actions={[{
              id: 'document',
              label: schema.title || 'Report settings',
              ariaLabel: 'Edit report-wide style and content settings',
              icon: <DocumentTextIcon className="h-4 w-4" />,
              variant: selection.kind === 'document' ? 'secondary' : 'ghost',
              pressed: selection.kind === 'document',
              onClick: () => onSelect({ kind: 'document' }),
              'data-testid': 'report-outline-document',
            }]}
          />
          {layout.coverPage && (
            <ToolbarActions
              size="sm"
              maxVisible={1}
              className={navigationActionClass}
              actions={[{
                id: 'cover-page',
                label: 'Cover page',
                ariaLabel: 'Select the cover page',
                icon: <RectangleStackIcon className="h-4 w-4" />,
                variant: selection.kind === 'coverPage' ? 'secondary' : 'ghost',
                pressed: selection.kind === 'coverPage',
                onClick: () => onSelect({ kind: 'coverPage' }),
                'data-testid': 'report-outline-cover-page',
              }]}
            />
          )}
        </Stack>

        <Stack gap={1}>
          <span className="px-2 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
            Structure
          </span>
          {schema.sections.length === 0 && (
            <p className="px-2 text-caption text-muted-foreground">
              Add sections on the Build tab — they appear here once the template has content to present.
            </p>
          )}
          {orderedSections.map((section, index) => {
            const kind = section.kind ?? 'section';
            const SectionIcon = SECTION_ICON[kind];
            const selected = selection.kind === 'section' && selection.sectionId === section.id;
            const hidden = section.hidden === true;
            return (
              <Cluster key={section.id} justify="between" align="center" gap={1}>
                <ToolbarActions
                  size="sm"
                  maxVisible={1}
                  className={`${navigationActionClass} ${hidden ? 'opacity-60' : ''}`}
                  actions={[{
                    id: section.id,
                    label: section.name || `${kind} ${index + 1}`,
                    ariaLabel: `Select ${kind} ${section.name || index + 1}${hidden ? ' (hidden from report)' : ''}`,
                    icon: <SectionIcon className="h-4 w-4" />,
                    variant: selected ? 'secondary' : 'ghost',
                    pressed: selected,
                    onClick: () => onSelect({ kind: 'section', sectionId: section.id }),
                    'data-testid': `report-outline-section-${section.id}`,
                  }]}
                />
                <Cluster gap={1} align="center" className="shrink-0">
                  {hidden && (
                    <Badge tone="neutral">
                      <EyeSlashIcon className="h-3 w-3" aria-hidden="true" />
                      <span className="sr-only">Hidden from report</span>
                    </Badge>
                  )}
                  <span className="px-1 text-caption tabular-nums text-muted-foreground">{section.fields.length}</span>
                  <RowActions
                    size="sm"
                    actions={[
                      {
                        id: 'move-up',
                        label: `Move ${section.name || 'section'} up in the report`,
                        icon: <ChevronUpIcon className="h-3.5 w-3.5" />,
                        iconOnly: true,
                        variant: 'ghost',
                        disabled: index === 0,
                        onClick: () => moveSection(section.id, 'up'),
                      },
                      {
                        id: 'move-down',
                        label: `Move ${section.name || 'section'} down in the report`,
                        icon: <ChevronDownIcon className="h-3.5 w-3.5" />,
                        iconOnly: true,
                        variant: 'ghost',
                        disabled: index === orderedSections.length - 1,
                        onClick: () => moveSection(section.id, 'down'),
                      },
                    ]}
                  />
                </Cluster>
              </Cluster>
            );
          })}
        </Stack>

        <Stack gap={1} className="px-2">
          <span className="text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
            Reusable blocks
          </span>
          <p className="text-caption text-muted-foreground">
            No reusable blocks yet — Content Tools will let you insert disclaimers, introductions and other
            controlled static blocks here.
          </p>
        </Stack>
      </Stack>
    </Surface>
  );
}

export interface ReportStudioInspectorProps {
  schema: FormSchemaV2;
  presentation: ReportPresentation;
  selection: ReportStudioSelection;
  settingsTab: ReportSettingsTab;
  onSettingsTabChange: (tab: ReportSettingsTab) => void;
  dispatch: (command: ReportStudioCommand) => void;
}

/**
 * Right panel: properties for the CURRENT SELECTION (this story's own
 * interaction contract) — not a permanently-mounted bulk settings panel.
 * `document` mounts the real, pre-existing Style/Content tabs; every other
 * selection shows a minimal, honest placeholder, since the controls that
 * belong there are each their own later story (see this file's header
 * comment for the full scope boundary and why that is not scope creep).
 */
export function ReportStudioInspector({
  schema,
  presentation,
  selection,
  settingsTab,
  onSettingsTabChange,
  dispatch,
}: ReportStudioInspectorProps): JSX.Element {
  if (selection.kind === 'document') {
    return (
      <Stack gap={0} className="h-full min-h-0">
        <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
          <SegmentedControl
            aria-label="Report settings tab"
            value={settingsTab}
            onChange={onSettingsTabChange}
            size="sm"
            options={[
              { value: 'style', label: 'Style' },
              { value: 'layout', label: 'Layout' },
              { value: 'headerFooter', label: 'Header/Footer' },
              { value: 'content', label: 'Content' },
              { value: 'media', label: 'Media' },
            ]}
          />
        </Surface>
        <div className="min-h-0 flex-1">
          <ReportDesignerSettings
            schema={schema}
            presentation={presentation}
            dispatch={dispatch}
            settingsTab={settingsTab}
          />
        </div>
      </Stack>
    );
  }

  if (selection.kind === 'coverPage') {
    const layout = reportLayoutOf(schema);
    return (
      <Surface as="section" variant="bare" className="min-h-full" data-testid="report-studio-inspector">
        <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
          <ContextHeader title="Cover page" headingLevel={2} />
        </Surface>
        <Stack gap={3} className="p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Show cover page</p>
              <p className="text-caption text-muted-foreground">A front-matter page before the report content.</p>
            </div>
            <Switch
              value={layout.coverPage}
              onChange={(next) =>
                dispatch({ type: 'patchReportLayout', label: 'Toggle cover page', payload: { coverPage: next } })
              }
              size="sm"
              aria-label="Show cover page"
            />
          </div>
          <p className="text-caption text-muted-foreground">
            Title, subtitle, hero-image and layout controls for the cover page itself ship with a later Report
            Studio tool group. Page format, orientation and margins (which also shape the cover page) are set on
            the Document panel&apos;s Layout tab.
          </p>
        </Stack>
      </Surface>
    );
  }

  const section = schema.sections.find((candidate) => candidate.id === selection.sectionId);
  return (
    <Surface as="section" variant="bare" className="min-h-full" data-testid="report-studio-inspector">
      <Surface as="section" variant="flat" padding="sm" className="rounded-none border-x-0 border-t-0">
        <ContextHeader title={section ? section.name || 'Untitled section' : 'Section'} headingLevel={2} />
      </Surface>
      <Stack gap={3} className="p-4">
        {section ? (
          <>
            <SectionDocumentTools schema={schema} section={section} dispatch={dispatch} />
            <div className="border-t border-border pt-3">
              <span className="px-0 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
                Presentation
              </span>
              <div className="mt-3">
                <SectionPresentationTools section={section} dispatch={dispatch} />
              </div>
            </div>
            {(section.kind ?? 'section') === 'table' && (
              <div className="border-t border-border pt-3">
                <span className="px-0 text-caption font-semibold uppercase tracking-eyebrow text-muted-foreground">
                  Table Tools
                </span>
                <div className="mt-3">
                  <TableTools section={section} dispatch={dispatch} />
                </div>
              </div>
            )}
            <div className="border-t border-border pt-3">
              <SectionResetTools section={section} dispatch={dispatch} />
            </div>
          </>
        ) : (
          // Edge case: "a referenced section/field was deleted from the
          // template" — the outline can only ever offer a section that is
          // still on `schema.sections` (it maps that array directly), but a
          // stale `selection` surviving an external reload
          // (`TemplateEditorPage.handleReload`) could still point at a
          // since-removed id for one render before the page's own
          // selection-reset effect clears it.
          <p className="text-caption text-muted-foreground">
            This section is no longer on the template.
          </p>
        )}
      </Stack>
    </Surface>
  );
}

/**
 * Story 4-114 — the real Document Tools controls for a selected section:
 * hide from report, page-flow (start on new page / keep with previous /
 * auto), and Move up/Move down (a second access point to the same
 * `setSectionReportOrder` command the outline's own buttons dispatch — the
 * outline and this panel can never disagree on order since both read
 * `currentReportOrderIds`). Deeper presentation (layout/columns/density/
 * heading/repeat/showEmpty/evidencePlacement/findingEmphasis) is `4-116`'s
 * own job — this section presents using the report's default settings for
 * everything this story does not own, same honest scope boundary `4-113`'s
 * own placeholder stated, now narrowed to what is actually still missing.
 */
function SectionDocumentTools({
  schema,
  section,
  dispatch,
}: {
  schema: FormSchemaV2;
  section: FormSchemaV2['sections'][number];
  dispatch: (command: ReportStudioCommand) => void;
}): JSX.Element {
  const hidden = section.hidden === true;
  const pageBreak: PageBreak = section.pageBreak ?? (section.kind === 'page' ? 'always' : 'auto');
  // Story 4-114 edge case — "a section configured to avoid splitting is
  // taller than one page" must be disabled with an explanation, not
  // silently ignored or allowed to produce broken output.
  const overflowsOnePage = useSectionOverflowsPage(section.id, schema);
  const orderedIds = currentReportOrderIds(schema);
  const orderIndex = orderedIds.indexOf(section.id);

  const move = (direction: 'up' | 'down') => {
    const swapWith = direction === 'up' ? orderIndex - 1 : orderIndex + 1;
    if (orderIndex === -1 || swapWith < 0 || swapWith >= orderedIds.length) return;
    const next = [...orderedIds];
    const a = next[orderIndex];
    const b = next[swapWith];
    if (a === undefined || b === undefined) return;
    next[orderIndex] = b;
    next[swapWith] = a;
    dispatch({
      type: 'setSectionReportOrder',
      label: `Move "${section.name || 'section'}" ${direction}`,
      payload: { orderedSectionIds: next },
    });
  };

  return (
    <>
      <Cluster justify="between" align="center">
        <Badge tone="neutral">{section.kind ?? 'section'}</Badge>
        <RowActions
          size="sm"
          actions={[
            {
              id: 'move-up',
              label: 'Move this section up in the report',
              icon: <ChevronUpIcon className="h-4 w-4" />,
              iconOnly: true,
              variant: 'ghost',
              disabled: orderIndex <= 0,
              onClick: () => move('up'),
            },
            {
              id: 'move-down',
              label: 'Move this section down in the report',
              icon: <ChevronDownIcon className="h-4 w-4" />,
              iconOnly: true,
              variant: 'ghost',
              disabled: orderIndex === -1 || orderIndex === orderedIds.length - 1,
              onClick: () => move('down'),
            },
          ]}
        />
      </Cluster>

      <div className="flex items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Hide from report</p>
          <p className="text-caption text-muted-foreground">
            Removes this section from the report only — it stays in the capture template unchanged.
          </p>
        </div>
        <Switch
          value={hidden}
          onChange={(next) =>
            dispatch({
              type: 'setSectionHidden',
              label: next ? `Hide "${section.name || 'section'}"` : `Show "${section.name || 'section'}"`,
              payload: { sectionId: section.id, hidden: next },
            })
          }
          size="sm"
          aria-label="Hide this section from the report"
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Page flow</p>
        <SegmentedControl<PageBreak>
          aria-label="Section page flow"
          value={pageBreak}
          size="sm"
          className="mt-1"
          onChange={(next) =>
            dispatch({
              type: 'setSectionPageBreak',
              label: `Change page flow for "${section.name || 'section'}"`,
              payload: { sectionId: section.id, pageBreak: next },
            })
          }
          options={PAGE_BREAK_OPTIONS.map((option) => ({
            ...option,
            ...(option.value === 'avoid' && overflowsOnePage
              ? {
                  disabled: true,
                  'aria-label':
                    'Keep with previous — disabled because this section is taller than one page and cannot be prevented from splitting',
                }
              : {}),
          }))}
        />
        {pageBreak === 'avoid' && overflowsOnePage && (
          <Callout tone="warning" className="mt-2" title="This section is taller than one page">
            Preventing it from splitting would produce broken output, so it will still be allowed to split across
            pages despite this setting.
          </Callout>
        )}
        {overflowsOnePage && pageBreak !== 'avoid' && (
          <p className="mt-1 text-caption text-muted-foreground">
            This section is taller than one page, so &quot;Keep with previous&quot; is unavailable for it.
          </p>
        )}
      </div>
    </>
  );
}

/**
 * Story 4-120 — "Reset section": scope is THIS section's own report-OUTPUT
 * fields only (hide-from-report, page-flow, and every Presentation/Table
 * Tools property above) — never its capture fields/answers, never any other
 * section. Confirmed before dispatch per `4-113`'s own interaction contract
 * ("destructive reset actions require confirmation and show their scope");
 * the reset itself is a normal undo-tracked command (`resetSectionReportFields`).
 */
function SectionResetTools({
  section,
  dispatch,
}: {
  section: FormSchemaV2['sections'][number];
  dispatch: (command: ReportStudioCommand) => void;
}): JSX.Element {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const sectionName = section.name || 'this section';
  return (
    <>
      <ToolbarActions
        size="sm"
        maxVisible={1}
        actions={[{
          id: 'reset-section',
          label: 'Reset section…',
          variant: 'destructive',
          onClick: () => setConfirmOpen(true),
        }]}
      />
      {confirmOpen && (
        <ResetConfirmDialog
          title={`Reset "${sectionName}"?`}
          description={`Resets "${sectionName}"'s visibility, page-flow and Presentation/Table Tools settings back to their defaults. Its captured questions and answers, and every OTHER section, are unaffected. You can undo this afterwards.`}
          onClose={() => setConfirmOpen(false)}
          onConfirm={() =>
            dispatch({
              type: 'resetSectionReportFields',
              label: `Reset "${sectionName}"`,
              payload: { sectionId: section.id },
            })
          }
        />
      )}
    </>
  );
}

// Story 4-116 — Section Presentation Tools: the real controls for all 8 new
// per-section report properties (`SectionDocumentTools` above already owns
// the 9th, `pageBreak`, since `4-114`). Every control dispatches
// `setSectionPresentationProperty` (`reportStudioCommands.ts`) — no control
// here mutates `schema`/`presentation` directly. `resolveSectionPresentation`
// (the SAME resolver both renderers read through) supplies the CURRENT,
// fully-defaulted value each control displays, so this panel can never show
// a value that disagrees with what actually renders.
//
// Unsupported combinations are DISABLED with a stated reason (this story's
// own done-when), not silently accepted:
//   * `layout` — a `kind:'table'` section's layout is structurally fixed to
//     Table (no control shown at all, just a stated reason); a non-table
//     section can never choose Table (nothing to build one from) or Grouped
//     list (no field-group metadata exists in the capture schema yet).
//   * `columns` — 'two' only applies to the List layout.
//   * `repeatHeading` — only a table section has a repeating header to
//     control.
//   * `evidencePlacement` — 'appendix' has no end-of-report section
//     structure to move evidence into yet.
//   * `findingEmphasis` — 'highlighted'/'summaryOnly' have nothing to act on
//     yet: findings are not associated with their originating section in
//     this report's data model (see `agents/shared/formSchema.js
//     #resolveSectionPresentation`'s own header comment for the full
//     rationale) — only 'standard' (today's unchanged behavior) is real.
const LAYOUT_OPTIONS: Array<{ value: SectionPresentation['layout']; label: string }> = [
  { value: 'list', label: 'List' },
  { value: 'groupedList', label: 'Grouped list' },
  { value: 'table', label: 'Table' },
  { value: 'summaryCards', label: 'Summary cards' },
  { value: 'narrative', label: 'Narrative' },
];
const COLUMNS_OPTIONS: Array<{ value: SectionPresentation['columns']; label: string }> = [
  { value: 'one', label: 'One' },
  { value: 'two', label: 'Two' },
];
const DENSITY_OPTIONS: Array<{ value: SectionPresentation['density']; label: string }> = [
  { value: 'comfortable', label: 'Comfortable' },
  { value: 'compact', label: 'Compact' },
  { value: 'condensed', label: 'Condensed' },
];
const HEADING_OPTIONS: Array<{ value: SectionPresentation['heading']; label: string }> = [
  { value: 'visible', label: 'Visible' },
  { value: 'hidden', label: 'Hidden' },
  { value: 'renamed', label: 'Renamed' },
  { value: 'numbered', label: 'Numbered' },
];
const SHOW_EMPTY_OPTIONS: Array<{ value: SectionPresentation['showEmpty']; label: string }> = [
  { value: 'show', label: 'Show' },
  { value: 'hide', label: 'Hide' },
  { value: 'notProvided', label: '"Not provided"' },
];
const EVIDENCE_PLACEMENT_OPTIONS: Array<{ value: SectionPresentation['evidencePlacement']; label: string }> = [
  { value: 'inline', label: 'Inline' },
  { value: 'afterResponse', label: 'After response' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'appendix', label: 'Appendix' },
];
const FINDING_EMPHASIS_OPTIONS: Array<{ value: SectionPresentation['findingEmphasis']; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: 'highlighted', label: 'Highlighted' },
  { value: 'summaryOnly', label: 'Summary only' },
];

function SectionPresentationTools({
  section,
  dispatch,
}: {
  section: FormSchemaV2['sections'][number];
  dispatch: (command: ReportStudioCommand) => void;
}): JSX.Element {
  const isTable = (section.kind ?? 'section') === 'table';
  const presentation = resolveSectionPresentation(section, section.kind ?? 'section');
  const sectionName = section.name || 'section';

  const setProp = (patch: Partial<SectionPresentation>, label: string) =>
    dispatch({ type: 'setSectionPresentationProperty', label, payload: { sectionId: section.id, patch } });

  return (
    <Stack gap={4}>
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Layout</p>
        {isTable ? (
          <p className="mt-1 text-caption text-muted-foreground">
            This section holds repeating table data — layout is fixed to Table.
          </p>
        ) : (
          <>
            <SegmentedControl<SectionPresentation['layout']>
              aria-label="Section layout"
              value={presentation.layout}
              size="sm"
              className="mt-1"
              onChange={(next) => setProp({ layout: next }, `Change layout for "${sectionName}"`)}
              options={LAYOUT_OPTIONS.map((option) => {
                if (option.value === 'table') {
                  return {
                    ...option,
                    disabled: true,
                    'aria-label': 'Table — disabled because this section has no repeating table data',
                  };
                }
                if (option.value === 'groupedList') {
                  return {
                    ...option,
                    disabled: true,
                    'aria-label':
                      "Grouped list — disabled because grouping needs field-group metadata that doesn't exist in the capture schema yet",
                  };
                }
                return option;
              })}
            />
            <p className="mt-1 text-caption text-muted-foreground">
              Grouped list isn&apos;t available yet — it needs field-group metadata the capture schema doesn&apos;t
              carry. Table layout only applies to sections with real repeating table data.
            </p>
          </>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Columns</p>
        <SegmentedControl<SectionPresentation['columns']>
          aria-label="Section columns"
          value={presentation.columns}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ columns: next }, `Change columns for "${sectionName}"`)}
          options={COLUMNS_OPTIONS.map((option) =>
            option.value === 'two' && presentation.layout !== 'list'
              ? { ...option, disabled: true, 'aria-label': 'Two — disabled because two-column layout only applies to the List layout' }
              : option,
          )}
        />
        {presentation.layout !== 'list' && (
          <p className="mt-1 text-caption text-muted-foreground">Two-column layout only applies to the List layout.</p>
        )}
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Density</p>
        <SegmentedControl<SectionPresentation['density']>
          aria-label="Section density"
          value={presentation.density}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ density: next }, `Change density for "${sectionName}"`)}
          options={DENSITY_OPTIONS}
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Heading</p>
        <SegmentedControl<SectionPresentation['heading']>
          aria-label="Section heading"
          value={presentation.heading}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ heading: next }, `Change heading for "${sectionName}"`)}
          options={HEADING_OPTIONS}
        />
        {presentation.heading === 'renamed' && (
          <Input
            className="mt-2"
            placeholder={section.name || 'Section name'}
            value={presentation.headingLabel ?? ''}
            onChange={(event) =>
              setProp({ headingLabel: event.target.value || null }, `Rename heading for "${sectionName}"`)
            }
            aria-label="Custom heading text"
          />
        )}
      </div>

      {!isTable && (
        // Story 4-118 — moved into the section-scoped "Table Tools" panel
        // (shown only for a `kind:'table'` section, immediately below this
        // one in the inspector) — a cleaner information architecture than a
        // permanently-disabled control sitting in the general panel every
        // OTHER section kind sees too. Left inline here, disabled, only for
        // a non-table section, so an author who lands on this panel first
        // still learns where repeating headers live rather than finding no
        // trace of the control at all.
        <p className="text-caption text-muted-foreground">
          Repeating table headers moved to Table Tools, shown below for a table section — this section has no
          repeating rows to control.
        </p>
      )}

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Empty values</p>
        <SegmentedControl<SectionPresentation['showEmpty']>
          aria-label="Empty value handling"
          value={presentation.showEmpty}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ showEmpty: next }, `Change empty-value handling for "${sectionName}"`)}
          options={SHOW_EMPTY_OPTIONS}
        />
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Evidence placement</p>
        <SegmentedControl<SectionPresentation['evidencePlacement']>
          aria-label="Evidence placement"
          value={presentation.evidencePlacement}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ evidencePlacement: next }, `Change evidence placement for "${sectionName}"`)}
          options={EVIDENCE_PLACEMENT_OPTIONS.map((option) =>
            option.value === 'appendix'
              ? {
                  ...option,
                  disabled: true,
                  'aria-label':
                    "Appendix — disabled because moving evidence into a separate end-of-report section isn't available yet",
                }
              : option,
          )}
        />
        <p className="mt-1 text-caption text-muted-foreground">
          Appendix placement isn&apos;t available yet — it needs a new end-of-report section structure this tool
          group didn&apos;t build.
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Finding emphasis</p>
        <SegmentedControl<SectionPresentation['findingEmphasis']>
          aria-label="Finding emphasis"
          value={presentation.findingEmphasis}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ findingEmphasis: next }, `Change finding emphasis for "${sectionName}"`)}
          options={FINDING_EMPHASIS_OPTIONS.map((option) =>
            option.value !== 'standard'
              ? {
                  ...option,
                  disabled: true,
                  'aria-label': `${option.label} — disabled because findings aren't linked to their originating section yet`,
                }
              : option,
          )}
        />
        <p className="mt-1 text-caption text-muted-foreground">
          Findings aren&apos;t associated with their originating section in this report yet, so only Standard has a
          real effect.
        </p>
      </div>
    </Stack>
  );
}

// Story 4-118 — Table Tools: the real controls for a `kind:'table'`
// section's own report-output properties. `repeatHeading`/`density` were
// ALREADY REAL, built and live-verified by `4-116` (see that story's own
// DONE annotation) — `repeatHeading` moves here from the general
// `SectionPresentationTools` panel above (a cleaner IA: it is meaningless
// for any non-table section, so it belongs in the panel only a table
// section ever sees); `density` stays in the general panel since it applies
// to every layout, not just tables. `tableColumns`/`overflow`/
// `showTotalsRow` are this story's own genuinely new fields. Every control
// dispatches the SAME generic `setSectionPresentationProperty` command
// `SectionPresentationTools` already uses (these are just three more fields
// on the same `SectionPresentation` shape) — no new command type was
// needed, matching `4-116`'s own "one generic command rather than N
// near-identical ones" precedent.
const EMPHASIS_OPTIONS: Array<{ value: TableColumnEmphasis; label: string }> = [
  { value: 'standard', label: 'Standard' },
  { value: 'primary', label: 'Primary' },
  { value: 'secondary', label: 'Secondary' },
  { value: 'compact', label: 'Compact' },
];
const OVERFLOW_OPTIONS: Array<{ value: SectionPresentation['overflow']; label: string }> = [
  { value: 'wrap', label: 'Wrap' },
  { value: 'truncate', label: 'Truncate' },
];

function TableTools({
  section,
  dispatch,
}: {
  section: FormSchemaV2['sections'][number];
  dispatch: (command: ReportStudioCommand) => void;
}): JSX.Element {
  const presentation = resolveSectionPresentation(section, section.kind ?? 'section');
  const sectionName = section.name || 'table';
  const fieldsByKey = new Map(section.fields.map((field) => [field.key, field]));
  const columns = presentation.tableColumns ?? [];
  const visibleCount = columns.filter((c) => c.visible).length;

  const setProp = (patch: Partial<SectionPresentation>, label: string) =>
    dispatch({ type: 'setSectionPresentationProperty', label, payload: { sectionId: section.id, patch } });

  const moveColumn = (key: string, direction: 'earlier' | 'later') => {
    const index = columns.findIndex((c) => c.key === key);
    const swapWith = direction === 'earlier' ? index - 1 : index + 1;
    if (index === -1 || swapWith < 0 || swapWith >= columns.length) return;
    const next = [...columns];
    const a = next[index];
    const b = next[swapWith];
    if (!a || !b) return;
    next[index] = b;
    next[swapWith] = a;
    setProp({ tableColumns: next }, `Reorder columns for "${sectionName}"`);
  };

  const toggleColumn = (key: string, visible: boolean) => {
    setProp(
      { tableColumns: columns.map((c) => (c.key === key ? { ...c, visible } : c)) },
      `${visible ? 'Show' : 'Hide'} a column in "${sectionName}"`,
    );
  };

  const setEmphasis = (key: string, emphasis: TableColumnEmphasis) => {
    setProp(
      { tableColumns: columns.map((c) => (c.key === key ? { ...c, emphasis } : c)) },
      `Change column emphasis in "${sectionName}"`,
    );
  };

  return (
    <Stack gap={4}>
      <Cluster justify="between" align="center" gap={3}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Repeat header on every page
          </p>
          <p className="text-caption text-muted-foreground">
            Repeats this table&apos;s header row on every physical page it spans, instead of forcing the whole
            table onto a fresh page.
          </p>
        </div>
        <Switch
          value={presentation.repeatHeading}
          onChange={(next) => setProp({ repeatHeading: next }, `Toggle repeat heading for "${sectionName}"`)}
          size="sm"
          aria-label="Repeat table header on every page"
        />
      </Cluster>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Columns</p>
        <p className="text-caption text-muted-foreground">
          Show, hide, reorder and emphasize this table&apos;s columns.
        </p>
        <Stack gap={2} className="mt-2">
          {columns.map((col, index) => {
            const field = fieldsByKey.get(col.key);
            const label = field?.label || col.key;
            return (
              <Surface key={col.key} variant="flat" padding="sm">
                <Stack gap={2}>
                  <Cluster justify="between" align="center" gap={2}>
                    <Cluster gap={2} align="center" className="min-w-0">
                      <Switch
                        value={col.visible}
                        onChange={(next) => toggleColumn(col.key, next)}
                        size="sm"
                        disabled={col.visible && visibleCount <= 1}
                        aria-label={`Show column "${label}"`}
                      />
                      <span className="truncate text-sm text-foreground">{label}</span>
                    </Cluster>
                    <RowActions
                      size="sm"
                      actions={[
                        {
                          id: 'earlier',
                          label: `Move "${label}" earlier`,
                          icon: <ChevronUpIcon className="h-3.5 w-3.5" />,
                          iconOnly: true,
                          variant: 'ghost',
                          disabled: index === 0,
                          onClick: () => moveColumn(col.key, 'earlier'),
                        },
                        {
                          id: 'later',
                          label: `Move "${label}" later`,
                          icon: <ChevronDownIcon className="h-3.5 w-3.5" />,
                          iconOnly: true,
                          variant: 'ghost',
                          disabled: index === columns.length - 1,
                          onClick: () => moveColumn(col.key, 'later'),
                        },
                      ]}
                    />
                  </Cluster>
                  <SegmentedControl<TableColumnEmphasis>
                    aria-label={`Emphasis for column "${label}"`}
                    value={col.emphasis}
                    size="sm"
                    onChange={(next) => setEmphasis(col.key, next)}
                    options={EMPHASIS_OPTIONS}
                  />
                </Stack>
              </Surface>
            );
          })}
        </Stack>
        <p className="mt-1 text-caption text-muted-foreground">
          Compact emphasis is automatically relaxed for a column containing photos, signatures or chips, so
          evidence is never compressed into an unusable cell.
        </p>
      </div>

      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Long values</p>
        <SegmentedControl<SectionPresentation['overflow']>
          aria-label="Cell overflow"
          value={presentation.overflow}
          size="sm"
          className="mt-1"
          onChange={(next) => setProp({ overflow: next }, `Change cell overflow for "${sectionName}"`)}
          options={OVERFLOW_OPTIONS}
        />
        <p className="mt-1 text-caption text-muted-foreground">
          Truncate clips a long value to one line — its full text still reaches the accessible and PDF text
          layers, only the visual line is clipped.
        </p>
      </div>

      <Cluster justify="between" align="center" gap={3}>
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Totals row</p>
          <p className="text-caption text-muted-foreground">
            Adds a summary row: totals numeric columns, counts chip/photo columns.
          </p>
        </div>
        <Switch
          value={presentation.showTotalsRow}
          onChange={(next) => setProp({ showTotalsRow: next }, `Toggle totals row for "${sectionName}"`)}
          size="sm"
          aria-label="Show totals row"
        />
      </Cluster>
    </Stack>
  );
}

export interface ReportStudioViewportToolsProps {
  previewMode: ReportPreviewMode;
  onPreviewModeChange: (mode: ReportPreviewMode) => void;
}

/** Header `contextTools` for the Report tab — same slot/placement the
 * Preview tab's own web/tablet/mobile switcher uses (`TemplateEditorPage`),
 * so "viewport/page format" lives in the ONE header this story's own
 * interaction contract names, not in the outline alongside document
 * structure. */
export function ReportStudioViewportTools({
  previewMode,
  onPreviewModeChange,
}: ReportStudioViewportToolsProps): JSX.Element {
  return (
    <SegmentedControl
      aria-label="Report preview mode"
      value={previewMode}
      onChange={onPreviewModeChange}
      size="sm"
      options={[
        { value: 'web', label: <span className="sr-only">Web</span>, 'aria-label': 'Web', icon: <ComputerDesktopIcon className="h-3.5 w-3.5" /> },
        { value: 'print', label: <span className="sr-only">Print</span>, 'aria-label': 'Print', icon: <PrinterIcon className="h-3.5 w-3.5" /> },
      ]}
    />
  );
}
